/**
 * Login page — renders email/password form with JWT authentication.
 * See wireframe: section 2.1 Login Screen
 */

import { router } from '@/router/router';

export function LoginPage(): HTMLElement {
  const page = document.createElement('div');
  page.className = 'login-page';

  page.innerHTML = `
    <div class="auth-container">
      <div class="auth-card">
        <div class="auth-brand">
          <span class="auth-logo">ROOFER</span>
          <p class="auth-tagline">Roof Design &amp; Planning</p>
        </div>

        <form id="login-form" class="auth-form" novalidate>
          <div class="form-group">
            <label for="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              autocomplete="email"
              required
              placeholder="you@company.com"
            />
            <span class="text-danger" id="email-error" role="alert"></span>
          </div>

          <div class="form-group">
            <label for="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              autocomplete="current-password"
              required
              placeholder="••••••••"
            />
            <span class="text-danger" id="password-error" role="alert"></span>
          </div>

          <div class="auth-options">
            <label class="checkbox-label">
              <input type="checkbox" id="remember-me" name="rememberMe" />
              <span>Remember me</span>
            </label>
            <a href="#/forgot-password" class="auth-link">Forgot password?</a>
          </div>

          <span class="text-danger" id="form-error" role="alert" style="margin-bottom:8px;display:block;"></span>

          <button type="submit" class="btn btn-primary btn-lg auth-submit" id="login-btn">
            Log In
          </button>
        </form>

        <p class="auth-footer">
          Don't have an account?
          <a href="#/register" class="auth-link">Register</a>
        </p>
      </div>
    </div>
  `;

  attachLoginHandlers(page);
  return page;
}

function attachLoginHandlers(page: HTMLElement): void {
  const form = page.querySelector<HTMLFormElement>('#login-form')!;
  const emailInput = page.querySelector<HTMLInputElement>('#email')!;
  const passwordInput = page.querySelector<HTMLInputElement>('#password')!;
  const emailError = page.querySelector<HTMLSpanElement>('#email-error')!;
  const passwordError = page.querySelector<HTMLSpanElement>('#password-error')!;
  const formError = page.querySelector<HTMLSpanElement>('#form-error')!;
  const submitBtn = page.querySelector<HTMLButtonElement>('#login-btn')!;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Clear errors
    emailError.textContent = '';
    passwordError.textContent = '';
    formError.textContent = '';

    const email = emailInput.value.trim();
    const password = passwordInput.value;

    // Basic validation
    let valid = true;
    if (!email) {
      emailError.textContent = 'Email is required.';
      valid = false;
    }
    if (!password) {
      passwordError.textContent = 'Password is required.';
      valid = false;
    }
    if (!valid) return;

    submitBtn.disabled = true;
    submitBtn.textContent = 'Logging in…';

    try {
      const { login } = await import('@/api/apiClient');
      await login({ email, password });
      router.replace('/dashboard');
    } catch (err) {
      formError.textContent =
        err instanceof Error ? err.message : 'Login failed. Please try again.';
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Log In';
    }
  });
}
